import React, { useState } from 'react'
import { Document, Page, Text, View, PDFDownloadLink, Image } from '@react-pdf/renderer'
import { CertificatePDFData, CertificatePDFGeneratorProps } from '../types'
import AlertPreviewModal from '../AlertPreviewModal'
import { styles } from './styles'
import logoDustre from '../../../../assets/dustre-logo.png'

interface CertificatePDFDocumentProps {
  certificate: CertificatePDFData
}

const CertificatePDFDocument: React.FC<CertificatePDFDocumentProps> = ({ certificate }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header} fixed>
        <Image src={logoDustre} />
        <Text style={styles.headerText}>Certificado #{certificate.id}</Text>
      </View>

      <View>
        <Text style={styles.title}>Certificado de Qualidade</Text>
        
        <View style={styles.dataGrid}>
          <View style={styles.dataItem}>
            <Text style={styles.label}>
              Nº do Certificado
              <Text style={styles.englishLabel}> (Certificate number)</Text>:
            </Text>
            <Text style={styles.value}>{certificate.id}</Text>
          </View>
          <View style={styles.dataItem}>
            <Text style={styles.label}>
              Cliente
              <Text style={styles.englishLabel}> (Customer)</Text>:
            </Text>
            <Text style={styles.value}>{certificate.customer}</Text>
          </View>
          <View style={styles.dataItem}>
            <Text style={styles.label}>
              Nº da Corrida
              <Text style={styles.englishLabel}> (Heat)</Text>:
            </Text>
            <Text style={styles.value}>{certificate.heatNumber}</Text>
          </View>
          <View style={styles.dataItem}>
            <Text style={styles.label}>
              Data
              <Text style={styles.englishLabel}> (Date)</Text>:
            </Text>
            <Text style={styles.value}>{certificate.date}</Text>
          </View>
          {certificate.description && (
            <View style={styles.dataItem}>
              <Text style={styles.label}>
                Descrição
                <Text style={styles.englishLabel}> (Description)</Text>:
              </Text>
              <Text style={styles.value}>{certificate.description}</Text>
            </View>
          )}
          {certificate.startingMaterial && (
            <View style={styles.dataItem}>
              <Text style={styles.label}>
                Material de partida
                <Text style={styles.englishLabel}> (Starting Material)</Text>:
              </Text>
              <Text style={styles.value}>{certificate.startingMaterial}</Text>
            </View>
          )}
        </View>

        {certificate.structures.map((structure, index) => (
          <View key={index} style={styles.structureContainer}>
            <Text style={styles.structureName}>{structure.name}</Text>
            
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                {structure.columns.map((column, colIndex) => (
                  <Text key={colIndex} style={[styles.tableCell, { width: `${100 / structure.columns.length}%` }]}>
                    {column.name}
                  </Text>
                ))}
              </View>

              <View style={styles.tableRow}>
                {structure.columns.map((column, colIndex) => (
                  <Text key={colIndex} style={[styles.tableCellLast, { width: `${100 / structure.columns.length}%` }]}>
                    {column.value.length ? column.value : '-'}
                  </Text>
                ))}
              </View>
            </View>
          </View>
        ))}

        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureText}>Responsável Técnico</Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureText}>Data</Text>
            <View style={styles.signatureLine} />
          </View>
        </View>
      </View>

      <View style={styles.footer} fixed>
        <Text style={styles.footerText}>Documento gerado automaticamente</Text>
        <Text style={styles.footerText} render={({ pageNumber, totalPages }) => 
          `Página ${pageNumber} de ${totalPages}`
        } />
      </View>
    </Page>

    {certificate.images && certificate.images.map((image, index) => (
      <Page key={index} size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src={logoDustre} />
          <Text style={styles.headerText}>Certificado #{certificate.id} - Imagem {index + 1}</Text>
        </View>

        <View style={styles.imagePageContent}>
          <Text style={styles.imageTitle}>{image.title}</Text>
          {image.subtitle && (
            <Text style={styles.imageSubtitle}>{image.subtitle}</Text>
          )}
          
          <View style={styles.imageContainer}>
            <Image 
              src={image.imageLink} 
              style={styles.certificateImage}
            />
          </View>

          <View style={styles.imageInfo}>
            <Text style={styles.imageInfoText}>
              Certificado: #{certificate.id}
            </Text>
            <Text style={styles.imageInfoText}>
              Cliente: {certificate.customer}
            </Text>
            <Text style={styles.imageInfoText}>
              Data: {certificate.date}
            </Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Documento gerado automaticamente</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => 
            `Página ${pageNumber} de ${totalPages}`
          } />
        </View>
      </Page>
    ))}
  </Document>
)

const CertificatePDFGenerator: React.FC<CertificatePDFGeneratorProps> = ({ 
  certificate, 
  fileName: customFileName,
}) => {
  const [showPreview, setShowPreview] = useState(false)
  const fileName = customFileName || `certificado_${certificate.id}_${certificate.customer.replace(/\s+/g, '_')}.pdf`

  return (
    <>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowPreview(true)}
        >
          Ver Detalhes
        </button>
        
        <PDFDownloadLink
          document={<CertificatePDFDocument certificate={certificate} />}
          fileName={fileName}
        >
          {({ loading }) => (loading ? 'Preparando PDF...' : 'Download PDF')}
        </PDFDownloadLink>
      </div>

      <AlertPreviewModal
        certificate={certificate}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </>
  )
}

export default CertificatePDFGenerator